import {ArrowPathIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {ReactElement, useCallback, useEffect, useState} from 'react';

import {WheelSelectorIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import NormalButton from './buttons/NormalButton';
import GameAudio from './GameAudio';

const ANIMATION_DURATION = 3; //seconds
// duration-[3000ms] // Don't remove this comment and keep it in sync with previous const. This is necessary for Tailwind to generate the class

type Props = {
  className?: string;
  choices: any[];
  wheelClass: string;
  initialChoice?: any;
  oneShot?: boolean;
  onChosen: (choice: any) => void;
  onSpinStart?: () => void;
  onSpinEnd?: () => void;
};

const WheelOfFate = ({
  className,
  choices,
  wheelClass,
  initialChoice,
  oneShot,
  onChosen,
  onSpinStart,
  onSpinEnd,
}: Props): ReactElement => {
  const [spinning, setSpinning] = useState(false);
  const [tries, setTries] = useState(0);
  const [rotation, setRotation] = useState(0); // deg

  useEffect(() => {
    if (initialChoice && rotation === 0) {
      const choiceIdx = choices.findIndex(c => c.id === initialChoice.id);
      if (choiceIdx === -1) return;
      const localRotation = ((choiceIdx * (360 / choices.length)) % 360) - 360 / (2 * choices.length);
      setRotation(localRotation);
    }
  }, [choices, initialChoice, rotation]);

  const spinWheel = useCallback(() => {
    const choiceIdx = Math.floor(Math.random() * choices.length);
    const choice = choices[choiceIdx];
    let localRotation = ((choiceIdx * (360 / choices.length)) % 360) - 360 / (2 * choices.length);
    if (localRotation < 0) localRotation += 360;

    const newTries = tries + 1;
    setSpinning(true);
    setRotation(newTries * 1080 + localRotation);
    setTries(newTries);
    onSpinStart && onSpinStart();

    setTimeout(() => {
      setSpinning(false);
      onChosen(choice);
      onSpinEnd && onSpinEnd();
    }, ANIMATION_DURATION * 1000);
  }, [choices, onChosen, onSpinEnd, onSpinStart, tries]);

  const hasSpinned = tries > 0 || !!initialChoice;

  return (
    <Col className={classNames('w-fit mobile:w-full mobile:mb-md', className)}>
      <GameAudio play={spinning} src="sounds/wheelSpinner.mp3" />
      <div className="flex justify-center w-full z-[1]">
        <div className="mt-sm">
          <WheelSelectorIcon className="h-[5rem] mobile:h-[4rem]" />
        </div>
      </div>
      {!(initialChoice && rotation === 0) && (
        <div className="relative">
          <div
            className={classNames(
              'flex h-[40rem] w-[40rem] mobile:h-[60vw] mobile:w-full font-sans bg-contain bg-no-repeat items-center -mt-[4rem] mobile:-mt-[3rem] transition-transform mx-auto bg-center',
              `duration-[${ANIMATION_DURATION * 1000}ms]`,
              wheelClass
            )}
            style={{transform: `rotate(${rotation}deg)`}}
          />
          {!spinning && !(hasSpinned && oneShot) && (
            <NormalButton
              className={classNames(
                'absolute inset-0 -top-md flex m-auto items-center justify-center',
                hasSpinned ? 'h-32 w-32' : 'h-56 mobile:h-40 w-56 mobile:w-40 mobile:text-base'
              )}
              onClick={spinWheel}
              light={hasSpinned}
            >
              {hasSpinned ? <ArrowPathIcon className="h-md w-md" /> : 'Click to spin'}
            </NormalButton>
          )}
        </div>
      )}
    </Col>
  );
};

export default WheelOfFate;
